import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Eye, 
  CheckCircle, 
  X, 
  MessageCircle, 
  Download,
  Send,
  Filter
} from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../../ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';

const ProposalsReceived = () => {
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState('all');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageText, setMessageText] = useState('');

  // Mock proposals data - GROUPED BY PROGRAM
  const proposalsByProgram = [
    {
      programTitle: 'Leadership Training for Middle Management',
      programId: 'prog-1',
      totalProposals: 2,
      proposals: [
        {
          id: 1,
          trainer: {
            id: 'trainer-1',
            name: 'Dr. Sarah Johnson',
            company: 'Leadership Excellence Inc',
            avatar: null,
          },
          submittedDate: '2024-09-26',
          status: 'pending'
        },
        {
          id: 2,
          trainer: {
            id: 'trainer-2',
            name: 'Michael Chen',
            company: 'Training Innovations Co',
            avatar: null,
          },
          submittedDate: '2024-09-25',
          status: 'pending'
        }
      ]
    },
    {
      programTitle: 'Technical Skills Development',
      programId: 'prog-2',
      totalProposals: 1,
      proposals: [
        {
          id: 3,
          trainer: {
            id: 'trainer-3',
            name: 'Emily Rodriguez',
            company: 'TechSkills Academy',
            avatar: null,
          },
          submittedDate: '2024-09-24',
          status: 'accepted'
        }
      ]
    },
    {
      programTitle: 'Sales Training Program',
      programId: 'prog-3',
      totalProposals: 1,
      proposals: [
        {
          id: 4,
          trainer: {
            id: 'trainer-4',
            name: 'David Thompson',
            company: 'Independent Trainer',
            avatar: null,
          },
          submittedDate: '2024-09-23',
          status: 'under_review'
        }
      ]
    },
    {
      programTitle: 'Customer Service Excellence',
      programId: 'prog-4',
      totalProposals: 1,
      proposals: [
        {
          id: 5,
          trainer: {
            id: 'trainer-5',
            name: 'Lisa Anderson',
            company: 'Service Excellence Ltd',
            avatar: null,
          },
          submittedDate: '2024-09-22',
          status: 'rejected'
        }
      ]
    }
  ];

  const getTotalProposals = () => {
    return proposalsByProgram.reduce((sum, program) => sum + program.proposals.length, 0);
  };

  const handleViewProposal = (proposal) => {
    navigate(`/proposal/${proposal.id}`);
  };

  const handleDownloadProposal = (proposal) => {
    alert(`Downloading proposal from ${proposal.trainer.name}`);
  };

  const handleAcceptProposal = (proposal) => {
    if (window.confirm(`Accept proposal from ${proposal.trainer.name}?`)) {
      alert('Proposal accepted!');
    }
  };

  const handleRejectProposal = (proposal) => {
    if (window.confirm(`Reject proposal from ${proposal.trainer.name}?`)) {
      alert('Proposal rejected!');
    }
  };

  const handleMessageTrainer = (proposal) => {
    console.log('Opening message modal for:', proposal.trainer.name);
    setSelectedTrainer(proposal.trainer);
    setMessageSubject('');
    setMessageText('');
    setShowMessageModal(true);
  };

  const handleSendMessage = () => {
    if (!messageText.trim()) {
      alert('Please enter a message');
      return;
    }
    console.log('Sending message to:', selectedTrainer.name);
    alert(`Message sent to ${selectedTrainer.name}!`);
    setShowMessageModal(false);
  };

  const handleTrainerClick = (trainerId) => {
    navigate(`/digitpro/${trainerId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <FileText className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Proposals Received</h1>
            <p className="text-gray-600">Review and manage trainer proposals for your requirements</p>
          </div>
        </div>
        <Badge variant="secondary" className="text-lg px-4 py-2">
          Total: {getTotalProposals()}
        </Badge>
      </div>

      {/* Programs and Their Proposals */}
      {proposalsByProgram.map((program) => (
        <Card key={program.programId} className="overflow-hidden">
          {/* Program Title Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">{program.programTitle}</h2>
              <Badge className="bg-white text-blue-700 font-semibold px-3 py-1">
                Total Proposals Rcvd: {program.totalProposals}
              </Badge>
            </div>
          </div>

          {/* Proposals Table for This Program */}
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b-2 border-gray-200">
                    <th className="p-4 text-left text-sm font-semibold text-gray-700">S.No</th>
                    <th className="p-4 text-left text-sm font-semibold text-gray-700">Trainer / Training Firm</th>
                    <th className="p-4 text-left text-sm font-semibold text-gray-700">Date of Submission</th>
                    <th className="p-4 text-center text-sm font-semibold text-gray-700">Proposal</th>
                    <th className="p-4 text-center text-sm font-semibold text-gray-700">Accept/Reject</th>
                    <th className="p-4 text-center text-sm font-semibold text-gray-700">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {program.proposals.map((proposal, index) => (
                    <tr key={proposal.id} className="border-b hover:bg-gray-50 transition-colors">
                      {/* S.No */}
                      <td className="p-4 text-center font-medium text-gray-700">
                        {index + 1}
                      </td>

                      {/* Trainer / Training Firm - Clickable */}
                      <td className="p-4">
                        <button
                          onClick={() => handleTrainerClick(proposal.trainer.id)}
                          className="flex items-center space-x-3 hover:bg-blue-50 p-2 rounded-lg transition-colors cursor-pointer"
                        >
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={proposal.trainer.avatar} alt={proposal.trainer.name} />
                            <AvatarFallback className="bg-blue-100 text-blue-600">
                              {proposal.trainer.name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="text-left">
                            <p className="font-semibold text-blue-600 hover:text-blue-800">
                              {proposal.trainer.name}
                            </p>
                            <p className="text-xs text-gray-600">{proposal.trainer.company}</p>
                          </div>
                        </button>
                      </td>

                      {/* Date of Submission */}
                      <td className="p-4 text-sm text-gray-700">
                        {new Date(proposal.submittedDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </td>

                      {/* Proposal - View & Download */}
                      <td className="p-4">
                        <div className="flex items-center justify-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewProposal(proposal)}
                            className="flex items-center space-x-1"
                          >
                            <Eye className="w-4 h-4" />
                            <span>View</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadProposal(proposal)}
                            className="flex items-center space-x-1"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>

                      {/* Accept/Reject */}
                      <td className="p-4">
                        <div className="flex items-center justify-center space-x-2">
                          {proposal.status === 'accepted' ? (
                            <Badge className="bg-green-100 text-green-800 px-3 py-1">
                              Accepted
                            </Badge>
                          ) : proposal.status === 'rejected' ? (
                            <Badge className="bg-red-100 text-red-800 px-3 py-1">
                              Rejected
                            </Badge>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleAcceptProposal(proposal)}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRejectProposal(proposal)}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <X className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </td>

                      {/* Message */}
                      <td className="p-4 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMessageTrainer(proposal)}
                          className="flex items-center space-x-1 mx-auto"
                        >
                          <MessageCircle className="w-4 h-4" />
                          <span>Message</span>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Message Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <MessageCircle className="w-5 h-5" />
              <span>Send Message</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Trainer Info */}
            {selectedTrainer && (
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={selectedTrainer.avatar} alt={selectedTrainer.name} />
                  <AvatarFallback className="bg-blue-100 text-blue-600">
                    {selectedTrainer.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{selectedTrainer.name}</p>
                  <p className="text-sm text-gray-600">{selectedTrainer.company}</p>
                </div>
              </div>
            )}

            {/* Subject */}
            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                placeholder="Enter subject..."
                value={messageSubject}
                onChange={(e) => setMessageSubject(e.target.value)}
                className="mt-2"
              />
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                rows={8}
                placeholder="Type your message here..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                className="mt-2"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowMessageModal(false)}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button 
                onClick={handleSendMessage}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Send className="w-4 h-4 mr-2" />
                Send Message
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProposalsReceived;